import React, {
    KeyboardEvent, ReactElement, useEffect, useRef,
    useState,
} from "react";
import ReactDOM from "react-dom";
import { Editor, Location } from "slate";
import { ReactEditor } from "slate-react";
import Styles from "./Editor.module.scss";
import KeyboardEventHandler from "./keyboard-event-handler";

export function Portal({ children }: {children: ReactElement}): ReactElement | null {
    return typeof document === "object" ? ReactDOM.createPortal(children, document.body) : null;
}

export interface Item {
    label: ReactElement;
    value: string;
}

interface ToolbarListProps {
    editor: ReactEditor;
    keyboardEvents: KeyboardEventHandler;
    startKey: string;
    filter: (filter: string) => Promise<Item[]> | Item[];
    onSelected: (item: string, startSelection: Location) => void;
    replacementSet?: Record<string, string>;
}

export function ToolbarList({
    editor, keyboardEvents, startKey, filter, onSelected, replacementSet,
}: ToolbarListProps): ReactElement {
    const ref = useRef<HTMLDivElement | null>();
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [show, setShow] = useState(false);
    const [list, setList] = useState<Item[]>();
    const [startSelection, setStartSelection] = useState<Location | null>();
    const [currentText, setCurrentText] = useState<string>();

    useEffect(() => {
        const el = ref.current;

        if (!el) {
            return;
        }

        if (!currentText || currentText.length < 2 || list?.isEmpty || !show) {
            el.removeAttribute("style");
            return;
        }

        const domSelection = window.getSelection();
        if (!domSelection) {
            return;
        }
        const domRange = domSelection.getRangeAt(0);
        const rect = domRange.getBoundingClientRect();
        el.style.opacity = "1";
        el.style.zIndex = "1100";
        el.style.top = `${rect.top + window.pageYOffset - el.offsetHeight}px`;
        el.style.left = `${rect.left + window.pageXOffset - el.offsetWidth / 2 + rect.width / 2}px`;
    }, [currentText, ref, list]);

    const resetState = () => {
        setList([]);
        setCurrentText("");
        setShow(false);
        setStartSelection(null);
    };

    const onClick = (index: number) => {
        if (!list || index == null) {
            return;
        }
        const value = list[index];
        if (!value) {
            return;
        }
        onSelected(value.value, startSelection as Location);
        resetState();
    };

    keyboardEvents.useShortcuts(shortcuts => {
        const complete = (event: KeyboardEvent) => {
            if (!list || selectedIndex == null) {
                return;
            }
            const value = list[selectedIndex];
            if (!value) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            onSelected(value.value, startSelection as Location);
            resetState();
        };

        shortcuts.on("Tab", complete);
        shortcuts.on("Enter", complete);
        shortcuts.on("ArrowUp", event => {
            if (!list || !show) {
                return;
            }
            setSelectedIndex(selected => ((selected - 1) < 0 ? list.length - 1 : (selected - 1)));
            event.preventDefault();
            event.stopPropagation();
        });
        shortcuts.on("ArrowDown", event => {
            if (!list || !show) {
                return;
            }
            setSelectedIndex(selected => {
                const sel = selected + 1;
                return (sel > list.length ? 0 : sel);
            });
            event.preventDefault();
            event.stopPropagation();
        });
        shortcuts.on(startKey, () => {
            if (!show) {
                setStartSelection(editor.selection?.anchor);
            }
            setSelectedIndex(0);
            setShow(current => !current);
        });

        shortcuts.on("Escape", event => {
            if (show) {
                event.stopPropagation();
            }
            resetState();
        });
        shortcuts.onKeyUp("", async event => {
            if ((!show || !/^[a-zA-Z0-9:()']$/.test(event.key)) && event.key !== "Backspace") {
                return;
            }
            const range = startSelection
                ? Editor.range(editor, startSelection as Location, editor.selection?.anchor)
                : undefined;
            const text = range ? Editor.string(editor, range) : "";

            setCurrentText(text);

            const newList = await filter(text);
            setList(newList);
        });
    }, [keyboardEvents, show, startSelection, list, selectedIndex]);

    keyboardEvents.useShortcuts(shortcuts => {
        if (replacementSet) {
            const handle = () => {
                const replacement = replacementSet[currentText?.trim() ?? ""];
                if (replacement) {
                    onSelected(replacement, startSelection as Location);
                }
                resetState();
            };
            shortcuts.on(" ", handle);
            shortcuts.on("Tab", handle);
        }
    }, [keyboardEvents, startSelection, list, currentText]);

    return (
        <Portal>
            <div
                ref={el => {
                    ref.current = el;
                }}
                className={Styles.toolbar}
                role="toolbar"
            >
                <ul className="list-unstyled text-white mb-0">
                    {list?.map((item, index) => (
                        <li
                            className={`p-2 ${index === selectedIndex ? "bg-light" : ""}`}
                            key={item.value}

                        >
                            <button
                                onClick={() => onClick(index)}
                                type="button"
                                className={`m-0 btn btn-none p-0 ${index === selectedIndex ? "text-primary" : "text-gray-200"}`}
                            >
                                {item.label}
                            </button>

                        </li>
                    ))}
                </ul>
            </div>
        </Portal>

    );
}

ToolbarList.defaultProps = {
    replacementSet: {},
};
